import React, { useEffect, useState, useCallback } from 'react';
import { Users, Circle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtLastSeen(iso, isID) {
  if (!iso) return isID ? 'Belum pernah online' : 'Never seen';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return isID ? 'Baru saja' : 'Just now';
  if (diffMin < 60) return `${diffMin}m ${isID ? 'lalu' : 'ago'}`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}j ${isID ? 'lalu' : 'ago'}`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}h ${isID ? 'lalu' : 'ago'}`;
  return d.toLocaleDateString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Presence panel for the current member's category. Shows a live list of
 * online peers plus a "last seen" roster of everyone else.
 *
 * Polls every 30s while mounted.
 */
export default function PresencePanel({ authHeader, lang }) {
  const isID = lang !== 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/members/category-presence?minutes=5&limit=100`, { headers: authHeader() });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.warn('presence fetch failed', err);
    }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="glass-card rounded-2xl p-6 text-center t-text-muted text-sm">Loading…</div>;
  if (!data || !data.category) return null;

  const online = data.online || [];
  const recent = data.recent || [];
  const total = data.total_in_category || 0;

  return (
    <div className="glass-card rounded-2xl overflow-hidden" data-testid="presence-panel">
      <div className="px-4 py-3 border-b t-border bg-[#D98A94]/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D98A94]" />
            <h3 className="text-sm font-bold t-text">
              {isID ? 'Siapa Online' : 'Who’s Online'} · {data.category}
            </h3>
          </div>
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest t-text-muted">
            <Circle className="w-1.5 h-1.5 fill-green-500 text-green-500 animate-pulse" />
            {online.length} / {total} {isID ? 'ONLINE' : 'ONLINE'}
          </div>
        </div>
      </div>

      <div className="p-3 max-h-[480px] overflow-y-auto space-y-3">
        {online.length > 0 && (
          <section>
            <div className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {isID ? 'ONLINE SEKARANG' : 'ONLINE NOW'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {online.map(r => <PresenceRow key={r.id} row={r} isID={isID} online />)}
            </div>
          </section>
        )}
        {recent.length > 0 && (
          <section>
            <div className="text-[10px] font-bold uppercase tracking-widest t-text-muted mb-2 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {isID ? 'TERAKHIR ONLINE' : 'LAST SEEN'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {recent.map(r => <PresenceRow key={r.id} row={r} isID={isID} />)}
            </div>
          </section>
        )}
        {online.length === 0 && recent.length === 0 && (
          <div className="text-center py-8 t-text-muted text-sm">
            {isID ? 'Belum ada aktivitas member.' : 'No member activity yet.'}
          </div>
        )}
        {data.never_seen_count > 0 && (
          <p className="text-[10px] t-text-muted italic text-center pt-2 border-t t-border">
            + {data.never_seen_count} {isID ? 'member belum pernah login' : 'members never logged in'}
          </p>
        )}
      </div>
    </div>
  );
}

function PresenceRow({ row, isID, online = false }) {
  const initials = (row.name || '?').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const isMale = (row.gender || '').toLowerCase().startsWith('m') || (row.gender || '').toLowerCase().startsWith('l');
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/40 transition-colors" data-testid={`presence-row-${row.id}`}>
      <div className="relative flex-shrink-0">
        {row.avatar ? (
          <img src={row.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${isMale ? 'bg-blue-500' : 'bg-pink-500'}`}>{initials}</div>
        )}
        {online && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold t-text truncate">{row.name}</div>
        <div className="text-[10px] t-text-muted truncate">
          {online ? (
            <span className="text-green-600 font-medium">● {isID ? 'Online' : 'Online'}</span>
          ) : (
            fmtLastSeen(row.last_seen_at, isID)
          )}
          {(row.city || row.country) && (
            <span> · {row.city || row.country}</span>
          )}
        </div>
      </div>
    </div>
  );
}
